/* eslint-disable max-lines-per-function */
import noop from 'lodash/noop';
import { useMemo } from 'react';

import { PractitionerView, PractitionerViewQuery } from '@dreryk/egabinet-personnel-frontend-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { Ordering, useTranslate } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   TableColumnConfiguration,
   TextPlaceholder,
   ValueRendererProps,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { practitionersTranslationKeys } from '../../../translations';
import { PractitionerUpdatePermissionsModalOpener } from '../permissions/PractitionerUpdatePermissionsModal.opener';

import { OptionsCell } from './cells/OptionsCell';
import { PhoneNumberCell } from './cells/PhoneNumberCell';
import { PractitionerCell } from './cells/PractitionerCell';
import { SpecialtyCell } from './cells/SpecialtyCell';
import { isPractitionerEditable } from './PractitionersTable.utils';
import { ConfidentialDataAccessCell } from './cells/ConfidentialDataAccessCell/ConfidentialDataAccessCell';
import { ConfidentialDataAccessCellHeader } from './cells/ConfidentialDataAccessCell/ConfidentialDataAccessCellHeader';
import { DefaultConfidentialityModeCell } from './cells/DefaultConfidentialityModeCell/DefaultConfidentialityModeCell';
import { DefaultConfidentialityModeCellHeader } from './cells/DefaultConfidentialityModeCell/DefaultConfidentialityModeCellHeader';

const DEFAULT_ORDERING: Ordering<PractitionerView>[] = [
   {
      property: 'lastName',
      descending: false,
   },
];

export const usePractitionersTableDataConfiguration = (): LazyLoadedDataConfigurationByCqrsQuery<PractitionerView> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<PractitionerView>(
      () => new PractitionerViewQuery(),
      DEFAULT_ORDERING
   );
};

export const usePractitionersTableColumns = (
   refetch: () => Promise<void>
): TableColumnConfiguration<PractitionerView>[] => {
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');
   const translate = useTranslate();

   return useMemo(() => {
      const isDemoMode = isDemo();

      const columnsWidth = createDynamicColumnsWidth({
         practitioner: '186px',
         phoneNumber: '25%',
         email: '25%',
         role: '25%',
         specialty: '25%',
         confidentialDataAccess: isDemoMode ? '0px' : '237px',
         defaultConfidentialityMode: isDemoMode ? '0px' : '195px',
         options: '35px',
      });

      return [
         {
            label: practitionersTranslationKeys.table.practitioner,
            width: columnsWidth.practitioner,
            accessor: noop,
            renderer: (props: ValueRendererProps<PractitionerView>) => {
               const editable = creatingAppointmentsAvailable && isPractitionerEditable(props.bean);
               return <PractitionerCell onUpdate={refetch} practitioner={props.bean} editable={editable} />;
            },
            name: 'lastName',
         },
         {
            label: practitionersTranslationKeys.table.phoneNumber,
            width: columnsWidth.phoneNumber,
            accessor: noop,
            renderer: (props: ValueRendererProps<PractitionerView>) => (
               <PhoneNumberCell phoneNumber={props.bean.phoneNumber} />
            ),
            name: 'phoneNumber',
            sortingDisabled: true,
            placeholder: TextPlaceholder.NO_DATA,
         },
         {
            label: practitionersTranslationKeys.table.email,
            width: columnsWidth.email,
            accessor: noop,
            renderer: (props: ValueRendererProps<PractitionerView>) => (
               <>{props.bean.emailAddress?.asString() ?? '-'}</>
            ),
            name: 'email',
            sortingDisabled: true,
         },
         {
            label: practitionersTranslationKeys.table.role,
            width: columnsWidth.role,
            accessor: noop,
            renderer: (props: ValueRendererProps<PractitionerView>) => (
               <>
                  {props.bean.jobPositions
                     .map((position) => translate(`${practitionersTranslationKeys.jobPositions[position]}`))
                     .join(', ')}
               </>
            ),
            name: 'jobPosition',
         },
         {
            label: practitionersTranslationKeys.table.specialty,
            width: columnsWidth.specialty,
            accessor: noop,
            renderer: ({ bean }: ValueRendererProps<PractitionerView>) =>
               bean.doctorSpecialties?.length ? <SpecialtyCell specialties={bean.doctorSpecialties} /> : null,
            name: 'specialty',
            sortingDisabled: true,
            placeholder: TextPlaceholder.NOT_APPLICABLE,
         },
         ...(isDemoMode
            ? []
            : [
                 {
                    headerRenderer: DefaultConfidentialityModeCellHeader,
                    width: columnsWidth.defaultConfidentialityMode,
                    accessor: noop,
                    renderer: ({ bean }: ValueRendererProps<PractitionerView>) =>
                       bean.confidentialityMode ? (
                          <DefaultConfidentialityModeCell
                             initialValue={bean.confidentialityMode}
                             practitionerId={bean.id}
                          />
                       ) : null,
                    name: 'defaultDataMode',
                    sortingDisabled: true,
                    placeholder: TextPlaceholder.NOT_APPLICABLE,
                 },
                 {
                    headerRenderer: ConfidentialDataAccessCellHeader,
                    width: columnsWidth.confidentialDataAccess,
                    accessor: noop,
                    renderer: ({ bean }: ValueRendererProps<PractitionerView>) =>
                       bean.confidentialAccessLevel ? (
                          <ConfidentialDataAccessCell
                             initialValue={bean.confidentialAccessLevel}
                             practitionerId={bean.id}
                          />
                       ) : null,
                    name: 'confidentialDataAccess',
                    sortingDisabled: true,
                    placeholder: TextPlaceholder.NOT_APPLICABLE,
                 },
              ]),
         {
            label: '',
            width: columnsWidth.options,
            accessor: noop,
            renderer: (props: ValueRendererProps<PractitionerView>) => {
               if (isPractitionerEditable(props.bean)) {
                  return (
                     <PractitionerUpdatePermissionsModalOpener practitioner={props.bean} onUpdate={refetch}>
                        {({ openModal }) => <OptionsCell openPractitionerUpdatePermissionsModal={openModal} />}
                     </PractitionerUpdatePermissionsModalOpener>
                  );
               }

               return null;
            },
            name: 'options',
            sortingDisabled: true,
         },
      ];
   }, [refetch, translate, creatingAppointmentsAvailable]);
};
